import ContentEntity from './content-entity.js';

/**
 * EventEntity — one community event (calendar + /events/:slug page).
 * Absorbs the old normalizeEvent/isEventActive/byStart helpers from
 * events-data.js. Schema doc lives in events-data.js; `EMPTY` mirrors it.
 */
class EventEntity extends ContentEntity {
    static TYPE = 'events';
    static S3_PATH = 'content.eventsData';

    // CTA registration states shown on the event page.
    static CTA = { RSVP: 'RSVP', DETAILS: 'Details' };

    static EMPTY = {
        id: '',
        status: true,
        featured: false,
        slug: '',
        language: 'en',
        category: 'event',
        title: '',
        subtitle: '',
        seo: { title: '', description: '', keywords: '', image: '' },
        startDate: '',
        endDate: '',
        startTime: null,
        endTime: null,
        allDay: false,
        timezone: 'America/New_York',
        location: { name: '', address: '', city: '', region: '', country: 'US', mapUrl: '', online: false, url: '' },
        image: '',
        thumbnail: '',
        gallery: [],
        intro: '',
        description: '',
        sections: [],
        agenda: [],
        highlights: [],
        speakers: [],
        faqList: [],
        tags: [],
        price: 0,
        currency: 'USD',
        registrationClosed: false,
        registration: { url: '', paymentUrl: '', label: 'RSVP', capacity: null, spotsLeft: null, deadline: null },
        organizer: { name: '', email: '', phone: null },
        publishedAt: '',
        updatedAt: '',
        source: 'local',
        externalId: null,
    };

    constructor(raw = {}) {
        super(raw);
        const R = EventEntity.resolveImg;

        if (!raw.image && !raw.seo?.image && !raw.gallery?.length) {
            console.warn('Event missing any image:', raw.slug || raw.id);
        }

        const image = R(raw.image) || R(raw.seo?.image) || null;
        this.image = image;
        this.gallery = Array.isArray(raw.gallery)
            ? raw.gallery
                .map((g) => (typeof g === 'string' ? { url: R(g), alt: '' } : { ...g, url: R(g.url) }))
                .filter((g) => g.url)
            : [];
        // Convenience: a single-day flag + a thumbnail fallback for the calendar pill.
        this.isMultiDay = !!(raw.endDate && raw.endDate !== raw.startDate);
        this.thumbnail = R(raw.thumbnail) || image || null;
    }

    // Chronological by start date, then start time — the calendar and agenda
    // both rely on this ordering.
    static compare(a, b) {
        const d = String(a.startDate).localeCompare(String(b.startDate));
        if (d !== 0) return d;
        return String(a.startTime || '').localeCompare(String(b.startTime || ''));
    }

    isPast(today = new Date().toISOString().slice(0, 10)) {
        const last = this.endDate || this.startDate || '';
        return !!last && String(last) < today;
    }

    isUpcoming(today = new Date().toISOString().slice(0, 10)) {
        return !this.isPast(today);
    }

    registrationOpen() {
        return !this.registrationClosed && !this.isPast();
    }
}

export default EventEntity;
